import { ref } from 'vue';

import workerService from '@/modules/worker/services/workerService';

interface DeactivateWorkersPayload {
	workerIds: string[];
	data: {
		reasonId: string;
		removeMission: boolean;
		comment?: string;
	};
}

/**
 * Composable to deactivate one or several workers in a single call.
 *
 * Pass a `workerIds` array containing 1..N ids. A single-worker flow
 * is just an array with one element, so there is no dedicated
 * "deactivate a single worker" composable.
 *
 * @returns `loading` reactive flag, true while the request is in flight.
 * @returns `deactivateWorkers` async action that performs the deactivation.
 *
 * @example
 * const { loading, deactivateWorkers } = useDeactivateWorkers();
 * await deactivateWorkers({
 *   workerIds: ["worker-1", "worker-2"],
 *   data: { reasonId: "reason-uuid", removeMission: true, comment: "End of mission" },
 * });
 */
export const useDeactivateWorkers = () => {
	const loading = ref(false);
	const deactivateWorkers = async ({
		workerIds,
		data,
	}: DeactivateWorkersPayload) => {
		try {
			loading.value = true;
			await workerService.deactivateWorkers(
				workerIds,
				data.reasonId,
				data.removeMission,
				data.comment
			);
		} finally {
			loading.value = false;
		}
	};

	return { loading, deactivateWorkers };
};
